/**
 * Washington State-Specific Diagnostic Questions
 * COMPLETE REBUILD - Using correct WAC 246-335 sections (Effective April 6, 2018)
 * 
 * CRITICAL CORRECTION:
 * Original file cited OLD/REPEALED section numbers (246-335-015, 025, 035, etc.)
 * These were replaced when WAC 246-335 was completely restructured in April 2018
 * 
 * CORRECT REGULATION STRUCTURE (Effective 4/6/2018):
 * - Part 1: General Requirements (246-335-300 through 246-335-355)
 * - Part 2: Home Care Agency Services (246-335-405 through 246-335-455)
 * - Part 3: Home Health Agency Services (246-335-505 through 246-335-560)
 * - Part 4: Hospice Agency Services (246-335-605 through 246-335-660)
 * - Part 5: Hospice Care Centers (246-335-705 through 246-335-768)
 * 
 * STATUTORY AUTHORITY:
 * - RCW 70.127 (In-Home Services Agencies)
 * - RCW 43.70.250
 * - RCW 18.88B (Home Care Aide Certification)
 * - RCW 74.34 (Abuse of Vulnerable Adults)
 * - RCW 43.43.830 (Background Checks)
 * 
 * VERIFIED SOURCES:
 * - https://app.leg.wa.gov/wac/default.aspx?cite=246-335
 * - https://doh.wa.gov/licenses-permits-and-certificates/facilities-z/home-care-agencies
 * - https://hcaw.org/doh-licensing-home-care-agencies/
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const WASHINGTON_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'WA-A1',
    itemId: 'A1',
    stateCode: 'WA',
    title: 'In-Home Services Agency License Status',
    description: 'Washington requires all in-home services agencies to be licensed by DOH under WAC 246-335',
    severity: 'high',
    regulationCode: '246-335-315',
    regulationName: 'License',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/licenses-permits-and-certificates/facilities-z/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active In-Home Services Agency license from Washington DOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Agency licenses are renewed annually per WAC 246-335-325',
      },
      {
        id: 'q2',
        question: 'What is your agency license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DOH license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Did you complete the required in-home services orientation class before applying for licensure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-320(1) - half-day class at DOH Tumwater campus',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Washington DOH In-Home Services Agency license is current and active per WAC 246-335-315.',
      atRisk: 'Your agency license expires within 90 days. Submit renewal per WAC 246-335-325 before expiration.',
      nonCompliant: 'Operating without a valid DOH license violates RCW 70.127 and can result in civil penalties up to $3,000 per day. Contact DOH immediately at (360) 236-4700.',
      unknown: 'Verify your agency license status at doh.wa.gov or call DOH at (360) 236-4700.',
    },
    stateNotes: [
      'Agency licenses are renewed annually (WAC 246-335-325)',
      'Orientation class required before initial application (WAC 246-335-320(1))',
      'DOH conducts unannounced surveys during licensure period (WAC 246-335-340)',
      'Application must be completed within 9 months or closed out (WAC 246-335-320(4))',
    ],
  },

  {
    id: 'WA-A2',
    itemId: 'A2',
    stateCode: 'WA',
    title: 'Plan of Operation Requirements',
    description: 'Washington requires a comprehensive plan of operation per WAC 246-335-415',
    severity: 'high',
    regulationCode: '246-335-415',
    regulationName: 'Plan of Operation',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your plan of operation include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'org_structure', label: 'Description of organizational structure' },
          { value: 'job_descriptions', label: 'Personnel job descriptions per WAC 246-335-425(2)' },
          { value: 'contractor_volunteer', label: 'Responsibilities of contractors and volunteers' },
          { value: 'service_areas', label: 'Services provided and geographic service areas' },
          { value: 'hours', label: 'Hours and days of operation' },
          { value: 'performance_eval', label: 'How performance evaluations will be conducted per WAC 246-335-425(13)-(14)' },
          { value: 'alternative_services', label: 'Process to inform clients of alternative services' },
          { value: 'record_preservation', label: 'Plan for preserving records per WAC 246-335-450(7)-(8)' },
          { value: 'time_frames', label: 'Time frames for filing documents in client records' },
          { value: 'emergency_prep', label: 'Emergency preparedness plan' },
          { value: 'administrator', label: 'Identified administrator with required responsibilities' },
        ],
        required: true,
        helpText: 'Required per WAC 246-335-415',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of operation meets WAC 246-335-415 requirements.',
      atRisk: 'Plan of operation is missing required elements per WAC 246-335-415. Missing: {missing}.',
      nonCompliant: 'Plan of operation does not meet WAC 246-335-415 requirements. Immediately update.',
      unknown: 'Review plan of operation against WAC 246-335-415 requirements.',
    },
    stateNotes: [
      'Must update plan of operation to reflect current practice (WAC 246-335-415(12))',
      'Administrator must be a home care employee (WAC 246-335-415(11))',
      'Emergency preparedness must address natural disasters, man-made incidents, and public health emergencies (WAC 246-335-415(10))',
    ],
  },

  {
    id: 'WA-A3',
    itemId: 'A3',
    stateCode: 'WA',
    title: 'Insurance Requirements',
    description: 'Washington requires commercial general liability insurance per WAC 246-335-320(2)(b)',
    severity: 'high',
    regulationCode: '246-335-320(2)(b)',
    regulationName: 'Insurance',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain current commercial general liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Proof of current insurance required per WAC 246-335-320(2)(b)',
      },
      {
        id: 'q2',
        question: 'What additional insurance coverage do you maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'professional', label: 'Professional liability/malpractice' },
          { value: 'workers_comp', label: 'Workers\' compensation (L&I)' },
          { value: 'auto', label: 'Commercial auto insurance' },
          { value: 'bond', label: 'Fidelity bond/dishonesty coverage' },
          { value: 'cyber', label: 'Cyber liability' },
        ],
      },
      {
        id: 'q3',
        question: 'When does your liability insurance policy expire?',
        inputType: 'date',
        helpText: 'Track expiration to ensure continuous coverage',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets WAC 246-335-320(2)(b) requirements.',
      atRisk: 'Basic insurance is in place but additional coverage is recommended.',
      nonCompliant: 'No commercial general liability insurance. This violates WAC 246-335-320(2)(b). Obtain coverage immediately.',
      unknown: 'Verify insurance coverage meets WAC requirements.',
    },
    stateNotes: [
      'Certificate of Insurance must be provided to DOH upon request',
      'Washington requires workers compensation through L&I for all employees',
      'Proof required with initial application and available upon survey',
    ],
  },

  {
    id: 'WA-A4',
    itemId: 'A4',
    stateCode: 'WA',
    title: 'Administrator Requirements',
    description: 'Washington requires a qualified administrator per WAC 246-335-415(11)',
    severity: 'high',
    regulationCode: '246-335-415(11)',
    regulationName: 'Administrator',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an identified administrator who is a home care employee?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Administrator must be an employee, not contractor per WAC 246-335-415(11)',
      },
      {
        id: 'q2',
        question: 'Does your administrator perform ALL required responsibilities? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'day_to_day', label: 'Oversee day-to-day operation and fiscal affairs' },
          { value: 'supervision', label: 'Provide management and supervision of services throughout all service areas' },
          { value: 'credentials', label: 'Ensure personnel are currently credentialed per WAC 246-335-425(5)' },
          { value: 'compliance', label: 'Ensure personnel comply with policies and procedures' },
          { value: 'quality', label: 'Implement quality improvement process per WAC 246-335-455' },
          { value: 'recordkeeping', label: 'Manage recordkeeping per WAC 246-335-450' },
          { value: 'supplies', label: 'Ensure supplies and equipment are available and maintained' },
          { value: 'accuracy', label: 'Ensure accuracy of public information materials' },
          { value: 'policies_access', label: 'Ensure current policies are accessible during hours of operation' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Required administrator responsibilities per WAC 246-335-415(11)(a)-(n)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 8 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets WAC 246-335-415(11) requirements.',
      atRisk: 'Administrator may not be performing all required responsibilities.',
      nonCompliant: 'No qualified administrator. This violates WAC 246-335-415(11). Designate a qualified employee as administrator.',
      unknown: 'Verify administrator qualifications against WAC 246-335-415(11).',
    },
    stateNotes: [
      'Administrator must be a home care employee per WAC 246-335-415(11)',
      'Must oversee day-to-day operations and fiscal affairs',
      'Responsible for ensuring personnel are credentialed per WAC 246-335-425(5)',
      'Must implement quality improvement per WAC 246-335-455',
    ],
  },

  {
    id: 'WA-A5',
    itemId: 'A5',
    stateCode: 'WA',
    title: 'Written Policies and Procedures',
    description: 'Washington requires specific written policies per WAC 246-335-425',
    severity: 'high',
    regulationCode: '246-335-425',
    regulationName: 'Personnel Policies',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Washington-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'discrimination', label: 'Employment criteria regarding discrimination per RCW 49.60' },
          { value: 'job_descriptions', label: 'Job descriptions with responsibilities and scope of practice' },
          { value: 'references', label: 'Reference verification process' },
          { value: 'contracting', label: 'Contracting process for contractors' },
          { value: 'credential_verification', label: 'Credential verification per WAC 246-335-425(5)' },
          { value: 'background_checks', label: 'Background checks per WAC 246-335-425(6)' },
          { value: 'ccs_determination', label: 'Character, competence, and suitability (CCS) determination per WAC 246-335-425(7)' },
          { value: 'mandatory_reporting', label: 'Mandatory reporter procedures per WAC 246-335-425(8)' },
          { value: 'skills_verification', label: 'Skills verification per WAC 246-335-425(9)' },
          { value: 'ongoing_training', label: 'Ongoing training per WAC 246-335-425(10)' },
          { value: 'food_safety', label: 'Safe food storage and handling per WAC 246-335-425(11)' },
          { value: 'infection_control', label: 'Infection control per WAC 246-335-425(12)' },
          { value: 'tb_screening', label: 'TB risk assessment and testing per WAC 246-335-425(12)(c)' },
          { value: 'performance_eval', label: 'Annual performance evaluations per WAC 246-335-425(13)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 13, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 10, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 10, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Policy manual meets WAC 246-335-425 requirements.',
      atRisk: 'Policy manual is missing required sections per WAC 246-335-425. Missing: {missing}.',
      nonCompliant: 'Multiple required policies are missing per WAC 246-335-425. DOH surveyors will cite incomplete policies.',
      unknown: 'Review policies against WAC 246-335-425 requirements.',
    },
    stateNotes: [
      'WAC 246-335-425 specifies all required personnel policies',
      'Must include discrimination policy per RCW 49.60',
      'Background check policy must comply with WAC 246-335-425(6)',
      'CCS determination procedures required per WAC 246-335-425(7)',
      'TB risk assessment and testing per WAC 246-335-425(12)(c)',
    ],
  },

  {
    id: 'WA-A6',
    itemId: 'A6',
    stateCode: 'WA',
    title: 'Emergency Preparedness Plan',
    description: 'Washington requires emergency preparedness addressing natural disasters and public health emergencies',
    severity: 'high',
    regulationCode: '246-335-415(10)',
    regulationName: 'Emergency Prep',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your emergency preparedness plan address the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'risk_assessment', label: 'Risk assessment and emergency planning' },
          { value: 'communication', label: 'Communication plan' },
          { value: 'coordination', label: 'Coordination of service delivery with emergency personnel' },
          { value: 'emergent_needs', label: 'Meeting emergent needs of clients' },
          { value: 'staff_training', label: 'Staff training on emergency procedures' },
          { value: 'natural_disasters', label: 'Natural disasters (earthquakes, wildfires, flooding)' },
          { value: 'man_made', label: 'Man-made incidents' },
          { value: 'public_health', label: 'Public health emergencies' },
        ],
        required: true,
        helpText: 'Required per WAC 246-335-415(10)',
      },
      {
        id: 'q2',
        question: 'When was your emergency plan last reviewed and updated?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past 12 months' },
          { value: '1_2_years', label: '1-2 years ago' },
          { value: 'over_2_years', label: 'More than 2 years ago' },
          { value: 'never', label: 'Never reviewed' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'within_year' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets WAC 246-335-415(10) requirements.',
      atRisk: 'Emergency preparedness plan may be incomplete or needs updating.',
      nonCompliant: 'Emergency preparedness plan does not meet WAC 246-335-415(10). Immediately develop comprehensive plan.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Must address natural disasters, man-made incidents, and public health emergencies per WAC 246-335-415(10)',
      'Include risk assessment and emergency planning',
      'Communication plan and coordination with emergency personnel required',
      'Staff training on emergency procedures required',
    ],
  },

  {
    id: 'WA-A7',
    itemId: 'A7',
    stateCode: 'WA',
    title: 'Quality Improvement Program',
    description: 'Washington requires a quality improvement program per WAC 246-335-455',
    severity: 'medium',
    regulationCode: '246-335-455',
    regulationName: 'Quality Improvement',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a formal quality improvement program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which quality metrics does your program track? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_outcomes', label: 'Client health outcomes' },
          { value: 'satisfaction', label: 'Client satisfaction surveys' },
          { value: 'complaints', label: 'Complaint tracking and resolution' },
          { value: 'incidents', label: 'Incident reports and trends' },
          { value: 'staff_competency', label: 'Staff competency evaluations' },
          { value: 'documentation', label: 'Documentation audits' },
          { value: 'infection_rates', label: 'Infection surveillance' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets WAC 246-335-455 requirements.',
      atRisk: 'Quality improvement program exists but may need enhancement.',
      nonCompliant: 'No quality improvement program. This violates WAC 246-335-455.',
      unknown: 'Review quality improvement requirements under WAC 246-335-455.',
    },
    stateNotes: [
      'QI program required per WAC 246-335-455',
      'Administrator responsible for implementing QI per WAC 246-335-415(11)',
      'Track client outcomes, satisfaction, complaints, and incidents',
      'Document QI activities and improvements made',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS & CREDENTIALS
  // ============================================
  {
    id: 'WA-B1',
    itemId: 'B1',
    stateCode: 'WA',
    title: 'Background Check Requirements',
    description: 'Washington requires background checks upon hire and every 2 years per WAC 246-335-425(6)',
    severity: 'high',
    regulationCode: '246-335-425(6)',
    regulationName: 'Background Checks',
    agencyName: 'Washington State Department of Social and Health Services',
    agencyAbbr: 'DSHS',
    agencyPhone: '(360) 902-0299',
    agencyUrl: 'https://www.dshs.wa.gov/bca',
    questions: [
      {
        id: 'q1',
        question: 'For ALL direct care employees, which background checks do you complete? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'dshs_bccu', label: 'DSHS Background Check Central Unit (BCCU) - Initial hire' },
          { value: 'wsp', label: 'Washington State Patrol (WSP) criminal history' },
          { value: 'disclosure', label: 'Disclosure statement' },
          { value: 'nar', label: 'Nurse Aide Registry check' },
          { value: 'oig', label: 'OIG/Medicaid exclusion list' },
        ],
        required: true,
        helpText: 'Home care agencies must use DSHS BCCU for initial checks, WSP for 2-year renewals per WAC 246-335-425(6)',
      },
      {
        id: 'q2',
        question: 'Do you renew background checks every 2 years for all direct care staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, renewed every 2 years' },
          { value: 'no', label: 'No, only at initial hire' },
        ],
        required: true,
        helpText: 'Background checks must be renewed every 2 years per WAC 246-335-425(6)',
      },
      {
        id: 'q3',
        question: 'Are background checks completed BEFORE any client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before client contact' },
          { value: 'no', label: 'No, sometimes employees start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before clearance (direct violation)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks, 2-year renewals, AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK if not renewing every 2 years or missing some checks
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check process meets WAC 246-335-425(6) requirements with 2-year renewals.',
      atRisk: 'Background check renewals not meeting 2-year requirement. Review WAC 246-335-425(6) for renewal requirements.',
      nonCompliant: 'VIOLATION: Employees working before background clearance OR required checks missing. Per WAC 246-335-425(6), NO client contact allowed until clearance received.',
      unknown: 'Audit background check process against WAC 246-335-425(6).',
    },
    stateNotes: [
      'Initial checks: DSHS BCCU for home care agencies',
      '2-year renewals: Washington State Patrol (WSP)',
      'Administrator and supervisor of direct care services also require checks',
      'Disclosure statements required per WAC 246-335-320(2)(d)',
    ],
  },

  {
    id: 'WA-B2',
    itemId: 'B2',
    stateCode: 'WA',
    title: 'Character, Competence, and Suitability (CCS) Determination',
    description: 'Washington requires CCS review for non-disqualifying background check results',
    severity: 'high',
    regulationCode: '246-335-425(7)',
    regulationName: 'CCS Determination',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have policies and procedures for conducting CCS determinations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required when background checks reveal non-disqualifying convictions, pending charges, or negative actions',
      },
      {
        id: 'q2',
        question: 'Does your CCS process consider the following factors? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'nature_crime', label: 'Nature and seriousness of the conviction or issue' },
          { value: 'vulnerability', label: 'Level of vulnerability of clients under their care' },
          { value: 'behaviors_since', label: 'Behaviors since the conviction or issue' },
          { value: 'pattern', label: 'Pattern of offenses or behaviors' },
          { value: 'years_since', label: 'Number of years since the conviction or issue' },
          { value: 'self_disclosure', label: 'Whether they self-disclosed the information' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Required factors per WAC 246-335-425(7)(b)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CCS determination process meets WAC 246-335-425(7) requirements.',
      atRisk: 'CCS process exists but may not include all required factors.',
      nonCompliant: 'No CCS determination process. This violates WAC 246-335-425(7).',
      unknown: 'Review CCS requirements under WAC 246-335-425(7).',
    },
    stateNotes: [
      'CCS review required for non-disqualifying background check results',
      'Must consider nature of crime, relevancy, time since offense, age at offense',
      'Evidence of treatment/rehabilitation per WAC 246-335-425(6)(b)(E)',
      'Document CCS determination in personnel file',
    ],
  },

  {
    id: 'WA-B3',
    itemId: 'B3',
    stateCode: 'WA',
    title: 'Credential Verification',
    description: 'Washington requires verification of all professional credentials per WAC 246-335-425(5)',
    severity: 'high',
    regulationCode: '246-335-425(5)',
    regulationName: 'Credentials',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://fortress.wa.gov/doh/providercredentialsearch/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional credentials through the DOH Provider Credential Search?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% verified before client contact' },
          { value: 'most', label: 'Most verified, some may be pending' },
          { value: 'some', label: 'Some not verified' },
          { value: 'unsure', label: 'Not sure how to check' },
        ],
        required: true,
        helpText: 'Verify at fortress.wa.gov/doh/providercredentialsearch',
      },
      {
        id: 'q2',
        question: 'Do you verify that long-term care workers who are not credentialed meet exemption criteria per WAC 246-980?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-425(5)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Credential verification meets WAC 246-335-425(5) requirements.',
      atRisk: 'Some credentials may be pending verification. Verify all before client contact.',
      nonCompliant: 'Using unverified personnel violates WAC 246-335-425(5). Immediately verify all credentials.',
      unknown: 'Check credentials at fortress.wa.gov/doh/providercredentialsearch',
    },
    stateNotes: [
      'Verify all credentials through DOH Provider Credential Search',
      'fortress.wa.gov/doh/providercredentialsearch for verification',
      'Non-credentialed LTC workers must meet exemption criteria per WAC 246-980',
      'Verification required before client contact',
    ],
  },

  {
    id: 'WA-B4',
    itemId: 'B4',
    stateCode: 'WA',
    title: 'Home Care Aide Certification',
    description: 'Washington requires Home Care Aide certification per RCW 18.88B',
    severity: 'high',
    regulationCode: '18.88B',
    regulationName: 'Home Care Aide',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/licenses-permits-and-certificates/professions-new-renew-or-update/home-care-aide',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Care Aides hold valid Washington DOH certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all are DOH certified' },
          { value: 'most', label: 'Most certified, some pending within 200 days' },
          { value: 'no', label: 'No / not sure about certification status' },
        ],
        required: true,
        helpText: 'Certification required within 200 days of hire per RCW 18.88B',
      },
      {
        id: 'q2',
        question: 'Do you track the 200-day certification deadline for new hires?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, with automated tracking' },
          { value: 'no', label: 'No formal tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs hold valid Washington DOH certifications per RCW 18.88B.',
      atRisk: 'Some HCAs pending certification. Ensure certification within 200 days of hire.',
      nonCompliant: 'HCAs working without certification violates RCW 18.88B. Verify all certifications immediately.',
      unknown: 'Verify all HCA certifications at doh.wa.gov.',
    },
    stateNotes: [
      'Home Care Aide certification required per RCW 18.88B',
      'Certification required within 200 days of hire',
      'Track 200-day deadline for new hires',
      'Verify certifications at doh.wa.gov',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL RECORDS
  // ============================================
  {
    id: 'WA-C1',
    itemId: 'C1',
    stateCode: 'WA',
    title: 'Personnel Record Requirements',
    description: 'Washington requires specific documents in personnel records per WAC 246-335-430',
    severity: 'high',
    regulationCode: '246-335-430',
    regulationName: 'Personnel Records',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do you maintain in personnel records? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'credential', label: 'Current practice certification, credential, or licensure' },
          { value: 'ltc_exemption', label: 'Documentation that non-credentialed LTC workers meet exemption criteria per WAC 246-980' },
          { value: 'background', label: 'Background check results and disclosure statements' },
          { value: 'ccs', label: 'CCS determination results (if applicable)' },
          { value: 'references', label: 'Reference verification' },
          { value: 'job_description', label: 'Job description' },
          { value: 'training', label: 'Training documentation including orientation' },
          { value: 'skills', label: 'Skills verification' },
          { value: 'tb', label: 'TB risk assessment and test results' },
          { value: 'performance', label: 'Annual performance evaluations' },
        ],
        required: true,
        helpText: 'Required per WAC 246-335-430',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel records contain all required documentation per WAC 246-335-430.',
      atRisk: 'Personnel records may be missing required documents. Missing: {missing}.',
      nonCompliant: 'Personnel records are missing critical documentation per WAC 246-335-430. DOH surveyors will cite incomplete files.',
      unknown: 'Audit personnel records against WAC 246-335-430 requirements.',
    },
    stateNotes: [
      'WAC 246-335-430 specifies required personnel record contents',
      'Must include credentials, background checks, CCS determination, training',
      'TB risk assessment and test results required',
      'Annual performance evaluations per WAC 246-335-425(13)',
    ],
  },

  {
    id: 'WA-C2',
    itemId: 'C2',
    stateCode: 'WA',
    title: 'Record Retention Requirements',
    description: 'Washington requires 6-year record retention per WAC 246-335-450',
    severity: 'medium',
    regulationCode: '246-335-450',
    regulationName: 'Client Records',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client and personnel records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer (meets WA requirement)' },
          { value: '4to5', label: '4-5 years' },
          { value: 'less4', label: 'Less than 4 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Washington requires 6-year retention per WAC 246-335-450',
      },
      {
        id: 'q2',
        question: 'Do you have a plan for preserving records if the agency ceases operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-450(7)-(8)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '6plus' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '4to5', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less4', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Washington\'s 6-year requirement per WAC 246-335-450.',
      atRisk: 'Record retention may not meet Washington\'s 6-year requirement.',
      nonCompliant: 'Record retention does not meet WAC 246-335-450 requirements.',
      unknown: 'Review record retention requirements under WAC 246-335-450.',
    },
    stateNotes: [
      'Washington requires 6-year retention per WAC 246-335-450',
      'Plan of operation must include record preservation plan per WAC 246-335-415',
      'Time frames for filing documents per WAC 246-335-450(7)-(8)',
      'Records must be accessible for DOH inspection',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'WA-D1',
    itemId: 'D1',
    stateCode: 'WA',
    title: 'TB Risk Assessment and Testing',
    description: 'Washington requires TB risk assessments for new employees and annually per WAC 246-335-425(12)(c)',
    severity: 'high',
    regulationCode: '246-335-425(12)(c)',
    regulationName: 'Tuberculosis',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct TB risk assessments using the DOH Adult TB Risk Assessment form?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, for all new employees and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No / not using DOH form' },
        ],
        required: true,
        helpText: 'DOH Adult TB Risk Assessment form required per WAC 246-335-425(12)(c)(i)',
      },
      {
        id: 'q2',
        question: 'Do you conduct TB testing when any risk factors are identified on the assessment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, testing if any boxes checked' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Testing required if any of the three boxes are checked on the DOH form',
      },
      {
        id: 'q3',
        question: 'Do employees receive TB-related training at hire or during orientation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-425(12)(c)(iii)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB risk assessment and testing meets WAC 246-335-425(12)(c) requirements.',
      atRisk: 'TB assessments must be conducted annually, not just at hire.',
      nonCompliant: 'TB risk assessment process does not meet WAC 246-335-425(12)(c) requirements.',
      unknown: 'Review TB requirements under WAC 246-335-425(12)(c).',
    },
    stateNotes: [
      'Use DOH Adult TB Risk Assessment form',
      'Risk assessment at hire and annually for all employees',
      'Testing required if any risk factors checked on form',
      'TB training required at hire or orientation per WAC 246-335-425(12)(c)(iii)',
    ],
  },

  {
    id: 'WA-D2',
    itemId: 'D2',
    stateCode: 'WA',
    title: 'Infection Control Program',
    description: 'Washington requires comprehensive infection control policies per WAC 246-335-425(12)',
    severity: 'high',
    regulationCode: '246-335-425(12)',
    regulationName: 'Infection Control',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection control program include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'standard_precautions', label: 'Standard precautions (hand hygiene, respiratory hygiene, cough etiquette)' },
          { value: 'ppe', label: 'PPE availability and use' },
          { value: 'tb', label: 'TB infection screening per WAC 246-335-425(12)(c)' },
          { value: 'communicable', label: 'Actions for personnel/clients with communicable disease symptoms (WAC 246-100, 246-101)' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure procedures per WAC 296-823' },
          { value: 'risk_assessment', label: 'Risk assessment for occupational exposure' },
          { value: 'hep_b', label: 'Hepatitis B vaccination policy' },
          { value: 'training', label: 'Infection control training for all staff' },
        ],
        required: true,
        helpText: 'Required per WAC 246-335-425(12)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets WAC 246-335-425(12) requirements.',
      atRisk: 'Infection control program may be incomplete. Review WAC 246-335-425(12) requirements.',
      nonCompliant: 'Infection control program does not meet WAC 246-335-425(12). Immediately develop comprehensive policies.',
      unknown: 'Review infection control requirements under WAC 246-335-425(12).',
    },
    stateNotes: [
      'Standard precautions including hand hygiene required per WAC 246-335-425(12)',
      'PPE availability and use required',
      'Bloodborne pathogen exposure procedures per WAC 296-823',
      'Training for all staff on infection control',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'WA-E1',
    itemId: 'E1',
    stateCode: 'WA',
    title: 'Skills Verification',
    description: 'Washington requires skills verification per WAC 246-335-425(9)',
    severity: 'high',
    regulationCode: '246-335-425(9)',
    regulationName: 'Skills Verification',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How do you verify skills of home care aides? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'written_testing', label: 'Written testing' },
          { value: 'skills_observation', label: 'Skills observation' },
          { value: 'previous_training', label: 'Evidence of previous training (LTC worker training per RCW 74.39A.074)' },
          { value: 'cna_training', label: 'CNA training per WAC 246-841-400' },
          { value: 'competency_eval', label: 'Competency evaluation' },
        ],
        required: true,
        helpText: 'Examples per WAC 246-335-425(9)',
      },
      {
        id: 'q2',
        question: 'Is skills verification completed before independent client care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Skills verification meets WAC 246-335-425(9) requirements.',
      atRisk: 'Skills verification process needs improvement.',
      nonCompliant: 'Skills verification does not meet WAC 246-335-425(9). Implement before client care.',
      unknown: 'Review skills verification requirements under WAC 246-335-425(9).',
    },
    stateNotes: [
      'Skills verification required per WAC 246-335-425(9)',
      'May include written testing, skills observation, competency evaluation',
      'Previous LTC worker training per RCW 74.39A.074 may count',
      'Must complete before independent client care',
    ],
  },

  {
    id: 'WA-E2',
    itemId: 'E2',
    stateCode: 'WA',
    title: 'Ongoing Training',
    description: 'Washington requires ongoing training pertinent to client care needs per WAC 246-335-425(10)',
    severity: 'medium',
    regulationCode: '246-335-425(10)',
    regulationName: 'Ongoing Training',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide ongoing training on the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_care', label: 'Client care needs' },
          { value: 'safety', label: 'Safety procedures' },
          { value: 'infection', label: 'Infection control updates' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'abuse', label: 'Abuse/neglect recognition and reporting' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'client_rights', label: 'Client rights' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document all training in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Ongoing training program meets WAC 246-335-425(10) requirements.',
      atRisk: 'Ongoing training program may need enhancement.',
      nonCompliant: 'Ongoing training program does not meet WAC 246-335-425(10) requirements.',
      unknown: 'Review ongoing training requirements under WAC 246-335-425(10).',
    },
    stateNotes: [
      'Ongoing training pertinent to client care needs per WAC 246-335-425(10)',
      'Document all training in personnel files',
      'Training should cover safety, infection control, abuse recognition',
      'Include client rights and emergency procedures',
    ],
  },

  {
    id: 'WA-E3',
    itemId: 'E3',
    stateCode: 'WA',
    title: 'Annual Performance Evaluations',
    description: 'Washington requires annual performance evaluations per WAC 246-335-425(13)',
    severity: 'medium',
    regulationCode: '246-335-425(13)',
    regulationName: 'Performance Evaluation',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct annual performance evaluations for ALL personnel and volunteers providing direct client care?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all annually' },
          { value: 'most', label: 'Most, some overdue' },
          { value: 'no', label: 'No formal evaluations' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do evaluations include on-site observation of care and skills specific to client needs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-425(13)',
      },
      {
        id: 'q3',
        question: 'Do you conduct annual evaluations of services provided by contractors?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not applicable' },
        ],
        helpText: 'Required per WAC 246-335-425(14)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Performance evaluation process meets WAC 246-335-425(13)-(14) requirements.',
      atRisk: 'Some performance evaluations may be overdue. Complete all annually.',
      nonCompliant: 'No formal performance evaluation process. This violates WAC 246-335-425(13).',
      unknown: 'Review performance evaluation requirements.',
    },
    stateNotes: [
      'Annual performance evaluations required per WAC 246-335-425(13)',
      'Must include on-site observation of care and skills',
      'Contractors also require annual evaluation per WAC 246-335-425(14)',
      'Document evaluations in personnel files',
    ],
  },

  {
    id: 'WA-E4',
    itemId: 'E4',
    stateCode: 'WA',
    title: 'Mandatory Reporter Training',
    description: 'Washington requires mandatory reporter training per WAC 246-335-425(8)',
    severity: 'high',
    regulationCode: '246-335-425(8)',
    regulationName: 'Mandatory Reporting',
    agencyName: 'Washington State DSHS Adult Protective Services',
    agencyAbbr: 'DSHS APS',
    agencyPhone: '(866) 363-4276',
    agencyUrl: 'https://www.dshs.wa.gov/altsa/home-and-community-services/adult-protective-services',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive training on mandatory reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all trained at orientation and annually' },
          { value: 'orientation', label: 'At orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse, neglect, or exploitation to DSHS APS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, trained on APS reporting (1-866-363-4276)' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Are staff aware they must also report failures to comply with WAC 246-335 to DOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-425(8)(a)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'orientation', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff trained on mandatory reporter duties per WAC 246-335-425(8) and RCW 74.34.',
      atRisk: 'Mandatory reporter training should be reinforced annually.',
      nonCompliant: 'No mandatory reporter training. Failure to train staff violates WAC 246-335-425(8). HCAs who fail to report can face criminal penalties.',
      unknown: 'Verify all staff received mandatory reporter training.',
    },
    stateNotes: [
      'Report suspected abuse immediately to DSHS APS: 1-866-363-4276',
      'Must also report failures to comply with WAC 246-335 to DOH',
      'Failure to report can result in gross misdemeanor charges',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'WA-F1',
    itemId: 'F1',
    stateCode: 'WA',
    title: 'Plan of Care Requirements',
    description: 'Washington requires a written plan of care for each client per WAC 246-335-440',
    severity: 'high',
    regulationCode: '246-335-440',
    regulationName: 'Plan of Care',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your care plans include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Measurable goals and outcomes' },
          { value: 'emergency', label: 'Emergency contact information' },
          { value: 'signature', label: 'Client/representative signature' },
          { value: 'responsible_party', label: 'Person responsible for care plan development' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care process meets WAC 246-335-440 requirements.',
      atRisk: 'Care plans may be incomplete or created after services begin.',
      nonCompliant: 'Services provided without written care plans violates WAC 246-335-440.',
      unknown: 'Review care plan process against WAC 246-335-440 requirements.',
    },
    stateNotes: [
      'Written plan of care required for every client per WAC 246-335-440',
      'Must be in place before services begin',
      'Include specific services, frequency, duration, and measurable goals',
      'Client/representative signature required',
    ],
  },

  {
    id: 'WA-F2',
    itemId: 'F2',
    stateCode: 'WA',
    title: 'Supervision of Home Care Services',
    description: 'Washington requires supervision of home care services per WAC 246-335-445',
    severity: 'high',
    regulationCode: '246-335-445',
    regulationName: 'Supervision',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated supervisor of direct care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does a supervisor evaluate each caregiver\'s performance?',
        inputType: 'select',
        options: [
          { value: 'every_90', label: 'Every 90 days or more frequently' },
          { value: 'every_180', label: 'Every 180 days' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No formal supervision schedule' },
        ],
      },
      {
        id: 'q3',
        question: 'Is supervision documented in personnel and client records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always documented' },
          { value: 'no', label: 'No / inconsistently' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no supervisor, no schedule, OR not documented
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'never' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT: has supervisor, regular supervision, and documented
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['every_90', 'every_180'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: annual supervision (infrequent but exists)
      { condition: 'equals', questionId: 'q2', value: 'annually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets WAC 246-335-445 requirements with proper documentation.',
      atRisk: 'Supervision frequency or documentation needs improvement.',
      nonCompliant: 'Caregivers not receiving required supervision per WAC 246-335-445.',
      unknown: 'Review supervision practices against WAC 246-335-445.',
    },
    stateNotes: [
      'Designated supervisor of direct care services required per WAC 246-335-445',
      'Regular performance evaluations required (every 90-180 days recommended)',
      'Document supervision in personnel and client records',
      'On-site observation included in supervision requirements',
    ],
  },

  {
    id: 'WA-F3',
    itemId: 'F3',
    stateCode: 'WA',
    title: 'Bill of Rights',
    description: 'Washington requires providing clients with Bill of Rights per WAC 246-335-435',
    severity: 'high',
    regulationCode: '246-335-435',
    regulationName: 'Client Rights',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ALL clients with a written Bill of Rights before or at the start of services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you obtain signed acknowledgment of receipt of the Bill of Rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Bill of Rights process meets WAC 246-335-435 requirements.',
      atRisk: 'Bill of Rights provided but acknowledgment not documented.',
      nonCompliant: 'Not providing Bill of Rights violates WAC 246-335-435.',
      unknown: 'Review Bill of Rights requirements under WAC 246-335-435.',
    },
    stateNotes: [
      'Written Bill of Rights required before or at start of services per WAC 246-335-435',
      'Obtain signed acknowledgment of receipt',
      'Keep signed copy in client record',
      'Must include all rights specified in WAC 246-335-435',
    ],
  },

  {
    id: 'WA-F4',
    itemId: 'F4',
    stateCode: 'WA',
    title: 'Delivery of Services Requirements',
    description: 'Washington requires specific service delivery standards per WAC 246-335-420',
    severity: 'high',
    regulationCode: '246-335-420',
    regulationName: 'Service Delivery',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have policies and procedures for the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'admission', label: 'Admission criteria' },
          { value: 'service_agreement', label: 'Written service agreements' },
          { value: 'discharge', label: 'Discharge procedures' },
          { value: 'referral', label: 'Referral to alternative services when unable to meet needs' },
          { value: 'grievance', label: 'Grievance and complaint procedures' },
          { value: 'abandonment', label: 'Prevention of client abandonment per WAC 246-335-310(7)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service delivery policies meet WAC 246-335-420 requirements.',
      atRisk: 'Service delivery policies may be incomplete.',
      nonCompliant: 'Service delivery policies do not meet WAC 246-335-420 requirements.',
      unknown: 'Review service delivery requirements under WAC 246-335-420.',
    },
    stateNotes: [
      'Admission criteria and service agreements required per WAC 246-335-420',
      'Discharge and referral procedures must be documented',
      'Grievance and complaint procedures required',
      'Prevention of client abandonment per WAC 246-335-310(7)',
    ],
  },

  // ============================================
  // SECTION G: CLIENT RECORDS
  // ============================================
  {
    id: 'WA-G1',
    itemId: 'G1',
    stateCode: 'WA',
    title: 'Client Record Requirements',
    description: 'Washington requires comprehensive client records per WAC 246-335-450',
    severity: 'high',
    regulationCode: '246-335-450',
    regulationName: 'Client Records',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do you maintain in client records? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'identification', label: 'Client identification information' },
          { value: 'service_agreement', label: 'Service agreement' },
          { value: 'care_plan', label: 'Plan of care' },
          { value: 'visit_notes', label: 'Visit/service documentation' },
          { value: 'emergency_contacts', label: 'Emergency contact information' },
          { value: 'bill_of_rights', label: 'Signed Bill of Rights acknowledgment' },
          { value: 'grievances', label: 'Grievances and resolutions' },
          { value: 'assessments', label: 'Assessments and reassessments' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are time frames established for filing documents per your plan of operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per WAC 246-335-415(9)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records meet WAC 246-335-450 requirements.',
      atRisk: 'Client records may be missing required elements.',
      nonCompliant: 'Client records do not meet WAC 246-335-450 requirements.',
      unknown: 'Review client record requirements under WAC 246-335-450.',
    },
    stateNotes: [
      'WAC 246-335-450 specifies all required client record elements',
      'Time frames for filing documents per WAC 246-335-415(9)',
      'Must include service agreement, care plan, visit documentation',
      'Emergency contacts and Bill of Rights acknowledgment required',
    ],
  },

  {
    id: 'WA-G2',
    itemId: 'G2',
    stateCode: 'WA',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '246-335-430',
    regulationName: 'Record Maintenance',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Agency license (annual renewal)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'background', label: 'Background checks (2-year renewal)' },
          { value: 'tb', label: 'TB risk assessments (annual)' },
          { value: 'hca_cert', label: 'HCA certifications' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'training', label: 'Training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}.',
      nonCompliant: 'No systematic document tracking. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Agency license renewed annually per WAC 246-335-325',
      'Background checks require 2-year renewal per WAC 246-335-425(6)',
      'TB risk assessments required annually per WAC 246-335-425(12)(c)',
      'HCA certifications must be tracked for 200-day deadline',
    ],
  },

  {
    id: 'WA-G3',
    itemId: 'G3',
    stateCode: 'WA',
    title: 'Audit-Ready Documentation',
    description: 'Files organized for DOH surveyor review per WAC 246-335-340',
    severity: 'medium',
    regulationCode: '246-335-340',
    regulationName: 'Survey',
    agencyName: 'Washington State Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(360) 236-4700',
    agencyUrl: 'https://doh.wa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are your files organized and accessible for DOH inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files organized with consistent structure' },
          { value: 'mostly', label: 'Most files organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any employee\'s or client\'s complete file within 5 minutes if DOH arrived for an unannounced survey?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'DOH conducts unannounced surveys per WAC 246-335-340',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'mostly', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Files are well-organized and ready for DOH inspection.',
      atRisk: 'File organization needs improvement. DOH surveyors expect immediate access.',
      nonCompliant: 'Disorganized files will result in survey deficiencies.',
      unknown: 'Review file organization against DOH survey requirements.',
    },
    stateNotes: [
      'DOH surveys are unannounced during licensure period',
      'Initial surveys are scheduled and announced',
      'Must cooperate with department during surveys per WAC 246-335-335(4)',
    ],
  },
];

export default WASHINGTON_QUESTIONS;